import { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Search, CheckCircle, XCircle } from 'lucide-react'; // Import XCircle para a mensagem de "não coberto"

const COVERAGE_IMG = "https://media.base44.com/images/public/69d9084d1d3aacacc2bd60ef/9a987ff9e_generated_1e776674.png";

// Define as cidades que são atendidas
const ALLOWED_CITIES = [
  'Santa Rosa',
  'Bom Jesus',
  'Vale do Paraíso',
  'Nova União',
  'Jorge Teixeira',
];

export default function CoverageSection() {
  const [neighborhood, setNeighborhood] = useState('');
  const [searchAttempted, setSearchAttempted] = useState(false); // Novo estado para rastrear se uma pesquisa foi feita
  const [isCityCovered, setIsCityCovered] = useState(false); // Novo estado para armazenar o status da cobertura
  const [filteredSuggestions, setFilteredSuggestions] = useState([]); // Novo estado para sugestões de autocompletar
  const [showSuggestions, setShowSuggestions] = useState(false); // Novo estado para controlar a visibilidade das sugestões

  const handleCheck = (e) => {
    e.preventDefault();
    setSearchAttempted(true); // Uma tentativa de pesquisa foi feita
    setShowSuggestions(false); // Esconde as sugestões após a pesquisa

    const normalizedNeighborhood = neighborhood.trim().toLowerCase();
    const found = ALLOWED_CITIES.some(city => city.toLowerCase() === normalizedNeighborhood);
    setIsCityCovered(found);
  };

  const handleInputChange = (e) => {
    const inputValue = e.target.value;
    setNeighborhood(inputValue);
    setSearchAttempted(false); // Reinicia o status da pesquisa ao mudar o input

    if (inputValue.trim() === '') {
      setFilteredSuggestions([]);
      setShowSuggestions(false);
    } else {
      const newSuggestions = ALLOWED_CITIES.filter(city =>
        city.toLowerCase().includes(inputValue.toLowerCase())
      );
      setFilteredSuggestions(newSuggestions);
      setShowSuggestions(newSuggestions.length > 0);
    }
  };

  const handleSelectSuggestion = (city) => {
    setNeighborhood(city);
    setShowSuggestions(false); // Esconde as sugestões após a seleção
  };

  return (
    <section id="cobertura" className="relative py-24 lg:py-32 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src={COVERAGE_IMG}
          alt="Cidade conectada com caminhos de luz de dados"
          className="w-full h-full object-cover opacity-15"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/90 to-background" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-2xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="font-inter text-xs font-semibold text-secondary uppercase tracking-widest">
              Área de Cobertura
            </span>
            <h2 className="font-poppins font-bold text-3xl lg:text-5xl mt-4 tracking-tight">
              Atendemos <span className="gradient-text">sua região</span>
            </h2>
            <p className="font-inter text-muted-foreground mt-4 text-lg">
              Verifique se seu bairro já está na nossa cobertura de fibra óptica. 
              Estamos expandindo todos os dias!
            </p>
          </motion.div>

          <motion.form
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            onSubmit={handleCheck}
            className="mt-10"
          >
            <div className="relative group">
              <div className="absolute -inset-px rounded-2xl bg-primary/50 opacity-0 group-focus-within:opacity-100 transition-opacity duration-500 blur-sm" />
              <div className="relative flex items-center gap-2 glass-card rounded-2xl p-2">
                <MapPin className="w-5 h-5 text-muted-foreground ml-4 flex-shrink-0" />
                <input
                  type="text"
                  value={neighborhood}
                  onChange={handleInputChange} // Usa o novo handler para input
                  onFocus={() => setShowSuggestions(neighborhood.trim() !== '' && filteredSuggestions.length > 0)} // Mostra sugestões ao focar se houver texto
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 300)} // Esconde sugestões ao desfocar, com um pequeno delay (aumentado para 300ms)
                  placeholder="Digite seu bairro ou cidade..."
                  className="flex-1 bg-transparent font-inter text-base text-foreground placeholder:text-muted-foreground/60 py-3 px-2 outline-none"
                />
                <button
                  type="submit"
                  className="flex-shrink-0 inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-primary text-white font-inter font-semibold text-sm hover:opacity-90 transition-opacity"
                >
                  <Search className="w-4 h-4" />
                  <span className="hidden sm:inline">Consultar</span>
                </button>
              </div>

              {/* Autocomplete Suggestions */}
              {showSuggestions && filteredSuggestions.length > 0 && (
                <div className="absolute z-20 w-full mt-2 rounded-xl glass-card border border-border/30 shadow-lg max-h-60 overflow-y-auto">
                  {filteredSuggestions.map((city, index) => (
                    <button
                      key={index}
                      type="button" // Importante para não submeter o formulário ao clicar
                      onClick={() => handleSelectSuggestion(city)}
                      className="block w-full text-left px-4 py-2 text-foreground hover:bg-primary/10 transition-colors duration-200"
                    >
                      {city}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </motion.form>

          {searchAttempted && isCityCovered && ( // Exibe mensagem de sucesso se coberto
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-green-500/10 border border-green-500/20"
            >
              <CheckCircle className="w-5 h-5 text-green-500" />
              <span className="font-inter text-sm text-green-400">
                Ótima notícia! Atendemos <strong>{neighborhood}</strong>. Entre em contato para contratar!
              </span>
            </motion.div>
          )}

          {searchAttempted && !isCityCovered && ( // Exibe mensagem de falha se não coberto
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-red-500/10 border border-red-500/20"
            >
              <XCircle className="w-5 h-5 text-red-500" />
              <span className="font-inter text-sm text-red-400">
                Desculpe, ainda não atendemos <strong>{neighborhood}</strong>. Mas estamos expandindo!
              </span>
            </motion.div>
          )}

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-10 flex flex-wrap justify-center gap-3"
          >
            {ALLOWED_CITIES.map((area) => ( // Usa ALLOWED_CITIES para exibição
              <span
                key={area}
                className="px-4 py-2 rounded-full text-xs font-inter font-medium text-muted-foreground bg-muted/50 border border-border/30"
              >
                {area}
              </span>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}